import { db } from '../firebase.config';
import { 
  collection, 
  addDoc, 
  serverTimestamp, 
  getDocs, 
  query, 
  where,
  DocumentData,
  QuerySnapshot,
  doc,
  deleteDoc,
  getDoc,
  updateDoc
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

export interface Class {
  id: string;
  name: string;
  description: string;
  teacherId: string;
  teacherName: string;
  teacherEmail?: string;
  deckIds: string[];
  createdAt: Date;
  students: string[];
}

export const createClass = async (
  name: string,
  description: string,
  teacherId: string,
  deckIds: string[]
): Promise<string> => {
  try {
    if (!teacherId) {
      throw new Error('User must be authenticated to create a class');
    }

    // Get user information from auth
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    // Get additional user information from Firestore if available
    let teacherName = '';
    let teacherEmail = currentUser?.email || null;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', teacherId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Check that displayName exists and is not empty
        if (userData.displayName && userData.displayName.trim() !== '') {
          teacherName = userData.displayName;
        } else if (userData.email) {
          // If name is not specified but email exists, use it
          teacherName = userData.email;
        }
        teacherEmail = userData.email || teacherEmail;
      } else {
        // If user document is not found, use data from auth
        if (currentUser?.displayName && currentUser.displayName.trim() !== '') {
          teacherName = currentUser.displayName;
        } else if (currentUser?.email) {
          teacherName = currentUser.email;
        }
      }
    } catch (err) {
      console.warn('Failed to get additional information about the teacher', err);
      // Use data from auth in case of error
      if (currentUser?.displayName && currentUser.displayName.trim() !== '') {
        teacherName = currentUser.displayName;
      } else if (currentUser?.email) {
        teacherName = currentUser.email;
      }
    }

    // Make sure teacher name is not empty
    if (!teacherName || teacherName.trim() === '') {
      teacherName = 'Unknown teacher';
    }

    const classRef = await addDoc(collection(db, 'classes'), {
      name,
      description,
      teacherId,
      teacherName,
      teacherEmail,
      deckIds,
      createdAt: serverTimestamp(),
      students: [],
    });

    return classRef.id;
  } catch (error) {
    console.error('Error creating class:', error);
    if (error instanceof Error) {
      if (error.message.includes('permission')) {
        throw new Error('You do not have permission to create classes. Please contact the administrator.');
      }
      throw error;
    }
    throw new Error('Failed to create class. Please try again later.');
  }
};

// Get all classes where user is a teacher
export const getTeacherClasses = async (userId: string): Promise<Class[]> => {
  try {
    if (!userId) {
      return [];
    }

    const q = query(
      collection(db, 'classes'),
      where('teacherId', '==', userId)
    );

    return processClassesSnapshot(await getDocs(q));
  } catch (error) {
    console.error('Error fetching teacher classes:', error);
    throw new Error('Failed to fetch classes. Please try again later.');
  }
};

// Get all classes where user is a student
export const getStudentClasses = async (userId: string): Promise<Class[]> => {
  try {
    if (!userId) {
      return [];
    }

    const q = query(
      collection(db, 'classes'),
      where('students', 'array-contains', userId)
    );

    return processClassesSnapshot(await getDocs(q));
  } catch (error) {
    console.error('Error fetching student classes:', error);
    throw new Error('Failed to fetch classes. Please try again later.');
  }
};

// Get all classes for a user (both as teacher and student)
export const getUserClasses = async (userId: string): Promise<{
  teacherClasses: Class[],
  studentClasses: Class[]
}> => {
  try {
    const [teacherClasses, studentClasses] = await Promise.all([
      getTeacherClasses(userId),
      getStudentClasses(userId)
    ]);

    return {
      teacherClasses,
      studentClasses
    };
  } catch (error) {
    console.error('Error fetching user classes:', error);
    throw new Error('Failed to fetch classes. Please try again later.');
  }
};

// Helper function to process QuerySnapshot
const processClassesSnapshot = (snapshot: QuerySnapshot<DocumentData>): Class[] => {
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as Class));
};

// Delete a class - only the teacher can delete their own class
export const deleteClass = async (classId: string, userId: string): Promise<boolean> => {
  try {
    if (!classId || !userId) {
      throw new Error('Class ID and user ID are required');
    }

    // Check if the user is the teacher of the class
    const classDoc = await getDoc(doc(db, 'classes', classId));
    if (!classDoc.exists()) {
      throw new Error('Class not found');
    }

    const classData = classDoc.data();
    if (classData.teacherId !== userId) {
      throw new Error('Only the teacher can delete this class');
    }

    // Delete the class
    await deleteDoc(doc(db, 'classes', classId));
    return true;
  } catch (error) {
    console.error('Error deleting class:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to delete class. Please try again later.');
  }
};

// Get path for a class (relative path)
export const getClassQrUrl = (classId: string): string => {
  return `/classes/${classId}`;
};

// Interface for deck learning progress
export interface UserProgress {
  userId: string;
  deckId: string;
  cardsLearned: number;
  totalCards: number;
  lastStudied: Date;
}

// Get progress of all students in a class
export const getClassStudentsProgress = async (classId: string, studentIds: string[], deckIds: string[]): Promise<UserProgress[]> => {
  try {
    if (!classId || !studentIds.length || !deckIds.length) {
      return [];
    }

    const progressCollection = collection(db, 'userProgress');
    const progressData: UserProgress[] = [];

    // For each student, get progress for all class decks
    for (const studentId of studentIds) {
      for (const deckId of deckIds) {
        const q = query(
          progressCollection,
          where('userId', '==', studentId),
          where('deckId', '==', deckId)
        );

        const progressSnap = await getDocs(q);
        
        if (!progressSnap.empty) {
          // If there's a progress record, add it to the results
          const progressDoc = progressSnap.docs[0].data();
          progressData.push({
            userId: studentId,
            deckId,
            cardsLearned: progressDoc.cardsLearned || 0,
            totalCards: progressDoc.totalCards || 0,
            lastStudied: progressDoc.lastStudied ? new Date(progressDoc.lastStudied.toDate()) : new Date()
          });
        } else {
          // If no record exists, add empty statistics
          progressData.push({
            userId: studentId,
            deckId,
            cardsLearned: 0,
            totalCards: 0,
            lastStudied: new Date()
          });
        }
      }
    }

    return progressData;
  } catch (error) {
    console.error('Error getting student progress:', error);
    throw new Error('Failed to get student progress. Please try again later.');
  }
};

// Функция для обновления флагов публичности колод классов пользователя
export const updateAllClassDecksPublicFlags = async (userId: string): Promise<void> => {
  try {
    console.log(`Updating public flags for all class decks of user ${userId}`);
    
    // Получаем колоды пользователя
    const userDecksRef = collection(db, 'userDecks', userId, 'decks');
    const userDecksSnapshot = await getDocs(userDecksRef);
    
    // Находим колоды, связанные с классами
    for (const deckDoc of userDecksSnapshot.docs) {
      const deckData = deckDoc.data();
      
      // Если колода связана с классом
      if (deckData.fromClassId) {
        try {
          // Получаем оригинальную колоду
          const originalDeckRef = doc(db, 'decks', deckDoc.id);
          const originalDeckSnapshot = await getDoc(originalDeckRef);
          
          if (originalDeckSnapshot.exists()) {
            const originalData = originalDeckSnapshot.data();
            
            // Если флаг публичности отличается, обновляем
            if (deckData.isPublic !== originalData.isPublic) {
              await updateDoc(deckDoc.ref, {
                isPublic: originalData.isPublic
              });
              console.log(`Updated isPublic flag for class deck ${deckDoc.id} to ${originalData.isPublic}`);
            }
          }
        } catch (deckError) {
          console.error(`Error updating isPublic flag for deck ${deckDoc.id}:`, deckError);
        }
      }
    }
    
    console.log(`Finished updating public flags for all class decks of user ${userId}`);
  } catch (error) {
    console.error('Error updating class decks public flags:', error);
  }
}; 